/**
 * 技能管理页面
 *
 * 显示系统技能和用户自定义技能列表
 * 支持创建、编辑、删除自定义技能
 * 支持预览技能内容
 */

import React, { useEffect, useCallback, useState } from 'react';
import { skillApi } from '../services/skillApi';
import { SkillUploadModal, SkillPreviewModal } from '../components/skill';
import type { Skill } from '../types/skill';

// 图标组件
const PlusIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const EditIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const TrashIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const EyeIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const RefreshIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

const SystemBadge = () => (
  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
    系统
  </span>
);

/**
 * 技能卡片组件
 */
interface SkillCardProps {
  skill: Skill;
  onEdit?: (skill: Skill) => void;
  onDelete?: (skill: Skill) => void;
  onPreview?: (skill: Skill) => void;
}

const SkillCard: React.FC<SkillCardProps> = ({ skill, onEdit, onDelete, onPreview }) => {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-lg font-medium text-gray-900">{skill.name}</h3>
            {skill.is_system && <SystemBadge />}
          </div>
          {skill.description && (
            <p className="mt-1 text-sm text-gray-800 line-clamp-2">
              {skill.description}
            </p>
          )}
          <div className="mt-2 flex items-center gap-4 text-sm text-gray-800">
            <span>来源：{skill.source === 'filesystem' ? '内置' : '数据库'}</span>
            {skill.updated_at && (
              <span>更新：{new Date(skill.updated_at).toLocaleDateString('zh-CN')}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 ml-4">
          <button
            onClick={() => onPreview?.(skill)}
            className="p-1.5 text-gray-800 hover:text-paper-700 hover:bg-paper-50 rounded"
            title="预览"
          >
            <EyeIcon />
          </button>
          {!skill.is_system && (
            <>
              <button
                onClick={() => onEdit?.(skill)}
                className="p-1.5 text-gray-800 hover:text-paper-700 hover:bg-paper-50 rounded"
                title="编辑"
              >
                <EditIcon />
              </button>
              <button
                onClick={() => onDelete?.(skill)}
                className="p-1.5 text-gray-800 hover:text-red-600 hover:bg-red-50 rounded"
                title="删除"
              >
                <TrashIcon />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * 技能管理页面组件
 */
export const SkillsPage: React.FC = () => {
  // 技能列表状态
  const [skills, setSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 模态框状态
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingSkill, setEditingSkill] = useState<Skill | null>(null);
  const [deletingSkill, setDeletingSkill] = useState<Skill | null>(null);
  const [previewingSkill, setPreviewingSkill] = useState<string | null>(null);

  // 加载技能列表
  const fetchSkills = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const skillList = await skillApi.listSkills();
      setSkills(skillList);
    } catch (err) {
      setError('加载技能列表失败');
      console.error('加载技能列表失败:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // 初始加载
  useEffect(() => {
    fetchSkills();
  }, [fetchSkills]);

  // 获取分类后的技能
  const systemSkills = skills.filter(s => s.is_system);
  const userSkills = skills.filter(s => !s.is_system);

  // 处理创建技能
  const handleCreate = useCallback(() => {
    setEditingSkill(null);
    setShowUploadModal(true);
  }, []);

  // 处理编辑技能
  const handleEdit = useCallback((skill: Skill) => {
    setEditingSkill(skill);
    setShowUploadModal(true);
  }, []);

  // 处理删除确认
  const handleDeleteConfirm = useCallback(async () => {
    if (!deletingSkill) return;

    try {
      await skillApi.deleteSkill(deletingSkill.id);
      setSkills(prev => prev.filter(s => s.id !== deletingSkill.id));
      setDeletingSkill(null);
    } catch (err) {
      console.error('删除技能失败:', err);
      setError('删除技能失败');
    }
  }, [deletingSkill]);

  // 处理上传成功
  const handleUploadSuccess = useCallback(() => {
    setShowUploadModal(false);
    setEditingSkill(null);
    fetchSkills();
  }, [fetchSkills]);

  // 处理预览技能
  const handlePreview = useCallback((skill: Skill) => {
    setPreviewingSkill(skill.name);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* 主内容区 */}
      <main className="max-w-5xl mx-auto px-6 py-8">
          {/* 标题和操作按钮 */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900">技能管理</h2>
            <div className="flex items-center gap-3">
              <button
                onClick={fetchSkills}
                className="flex items-center gap-2 px-4 py-2 text-gray-800 hover:bg-gray-100 rounded-md font-medium"
                title="刷新列表"
              >
                <RefreshIcon />
                刷新
              </button>
              <button
                onClick={handleCreate}
                className="flex items-center gap-2 bg-ink-800 hover:bg-ink-700 text-white px-4 py-2 rounded-md font-medium shadow-sm hover:shadow-md active:scale-[0.98] transition-all duration-150"
              >
                <PlusIcon />
                新建技能
              </button>
            </div>
          </div>

          {/* 错误状态 */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md text-red-700">
              {error}
              <button onClick={() => setError(null)} className="ml-4 underline">
                关闭
              </button>
            </div>
          )}

          {/* 加载状态 */}
          {loading && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-paper-500"></div>
              <p className="mt-4 text-gray-800">加载中...</p>
            </div>
          )}

          {/* 系统技能区域 */}
          {!loading && systemSkills.length > 0 && (
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-4">系统技能</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {systemSkills.map((skill) => (
                  <SkillCard
                    key={skill.id}
                    skill={skill}
                    onPreview={handlePreview}
                  />
                ))}
              </div>
            </div>
          )}

          {/* 用户自定义技能区域 */}
          {!loading && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">自定义技能</h3>
              {userSkills.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {userSkills.map((skill) => (
                    <SkillCard
                      key={skill.id}
                      skill={skill}
                      onEdit={handleEdit}
                      onDelete={setDeletingSkill}
                      onPreview={handlePreview}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
                  <p className="text-gray-800 mb-4">还没有自定义技能</p>
                  <button
                    onClick={handleCreate}
                    className="text-paper-700 hover:text-paper-800 font-medium"
                  >
                    创建第一个技能
                  </button>
                </div>
              )}
            </div>
        )}
      </main>

      {/* 技能上传/编辑模态框 */}
      <SkillUploadModal
        isOpen={showUploadModal}
        onClose={() => {
          setShowUploadModal(false);
          setEditingSkill(null);
        }}
        onSuccess={handleUploadSuccess}
        skill={editingSkill}
      />

      {/* 技能预览模态框 */}
      <SkillPreviewModal
        isOpen={!!previewingSkill}
        skillName={previewingSkill}
        onClose={() => setPreviewingSkill(null)}
      />

      {/* 删除确认对话框 */}
      {deletingSkill && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">确认删除</h3>
            <p className="text-gray-800 mb-6">
              确定要删除技能「{deletingSkill.name}」吗？此操作不可撤销。
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeletingSkill(null)}
                className="px-4 py-2 text-gray-800 hover:bg-gray-100 rounded-md"
              >
                取消
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md"
              >
                删除
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SkillsPage;
